import { useState, useMemo } from "react";
import { usePageTitle } from "../hooks/usePageTitle";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";

// Governance Delay Cost Calculator - formula and bands per the Groundwork Market brief:
// Direct Cost = attendees x hourly rate x meeting duration x times repeated
// Delivery Impact = Direct Cost x delay-length multiplier
// Total = Direct Cost + Delivery Impact
//
// Every input is a discrete band (not a continuous slider) on purpose - the old version
// let visitors drag a "budget" slider that never fed the formula at all, and dressed up a
// synthetic score in false precision. Bands keep the output honest about how coarse the
// underlying estimate actually is.

const ATTENDEE_OPTIONS = [
  { label: "1-5", value: 3 },
  { label: "6-10", value: 8 },
  { label: "11-20", value: 15 },
  { label: "20+", value: 25 },
];

const SALARY_OPTIONS = [
  { label: "$70k", value: 70000 },
  { label: "$90k", value: 90000 },
  { label: "$120k", value: 120000 },
  { label: "$150k+", value: 150000 },
];

const DURATION_OPTIONS = [
  { label: "30 min", value: 0.5 },
  { label: "1 hr", value: 1 },
  { label: "90 min", value: 1.5 },
  { label: "2 hrs", value: 2 },
];

const REPEAT_OPTIONS = [
  { label: "1", value: 1 },
  { label: "2", value: 2 },
  { label: "3", value: 3 },
  { label: "4+", value: 4 },
];

const DELAY_OPTIONS = [
  { label: "< 1 week", value: 1 },
  { label: "1-2 weeks", value: 1.5 },
  { label: "2-4 weeks", value: 2 },
  { label: "1-3 months", value: 3 },
  { label: "3+ months", value: 5 },
];

// Loaded hourly rate from an annual salary band: standard full-time year (2,080 hrs)
// with a 1.3x on-cost loading (super, overhead) - shown on-page so the number isn't
// presented without its basis.
const HOURS_PER_YEAR = 2080;
const LOADING_FACTOR = 1.3;
const salaryToHourlyRate = (salary) => (salary / HOURS_PER_YEAR) * LOADING_FACTOR;

function formatCurrency(val) {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(val);
}

function interpretCost(total) {
  if (total < 1000) {
    return { band: "green", label: "Minor governance friction", color: "var(--green)", bg: "var(--green-light)" };
  }
  if (total <= 10000) {
    return { band: "amber", label: "Governance delays are impacting delivery", color: "var(--amber)", bg: "var(--amber-light)" };
  }
  return { band: "red", label: "Decision-making may be creating more cost than it prevents", color: "#EF4444", bg: "#FEE2E2" };
}

function SegmentedField({ label, options, value, onChange }) {
  return (
    <div>
      <label style={{ fontSize: 14, fontWeight: 600, color: "var(--navy)", display: "block", marginBottom: 8 }}>
        {label}
      </label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.label}
              type="button"
              onClick={() => onChange(opt.value)}
              aria-pressed={active}
              style={{
                padding: "8px 14px",
                borderRadius: "var(--radius)",
                border: active ? "1px solid var(--lilac-deep)" : "1px solid var(--border-strong)",
                background: active ? "var(--lilac-deep)" : "var(--surface-0)",
                color: active ? "#fff" : "var(--navy)",
                fontSize: 13,
                fontWeight: 600,
                transition: "all var(--transition-fast)",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function DelayCostCalculator() {
  usePageTitle("Governance Delay Cost Calculator");
  const [attendees, setAttendees] = useState(ATTENDEE_OPTIONS[1].value);
  const [salary, setSalary] = useState(SALARY_OPTIONS[1].value);
  const [duration, setDuration] = useState(DURATION_OPTIONS[1].value);
  const [repeats, setRepeats] = useState(REPEAT_OPTIONS[1].value);
  const [delay, setDelay] = useState(DELAY_OPTIONS[1].value);

  const results = useMemo(() => {
    const hourlyRate = salaryToHourlyRate(salary);
    const directCost = attendees * hourlyRate * duration * repeats;
    const deliveryImpact = directCost * delay;
    const totalCost = directCost + deliveryImpact;
    return { hourlyRate, directCost, deliveryImpact, totalCost };
  }, [attendees, salary, duration, repeats, delay]);

  const interpretation = interpretCost(results.totalCost);

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "3rem 24px 5rem" }}>
      {/* Header */}
      <section style={{ textAlign: "center", marginBottom: "3rem" }}>
        <div style={{ marginBottom: 16 }}>
          <Badge>Governance Delay Cost Calculator</Badge>
        </div>
        <h1
          style={{
            fontFamily: "var(--font-voice)",
            fontSize: "clamp(30px, 4.5vw, 42px)",
            color: "var(--navy)",
            margin: "0 0 14px",
            lineHeight: 1.25,
          }}
        >
          How much are delayed decisions really costing you?
        </h1>
        <p
          style={{
            fontSize: 16,
            color: "var(--text-secondary)",
            maxWidth: 600,
            margin: "0 auto",
            lineHeight: 1.6,
          }}
        >
          Most organisations track payroll, licensing, and project budgets closely. Almost nobody
          tracks the cost of one decision, discussed again and again without landing. Pick the
          bands closest to a real decision you've had to revisit.
        </p>
      </section>

      {/* Main Grid: Inputs Left, Outputs Right */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 24,
          alignItems: "stretch",
        }}
      >
        {/* Input Card */}
        <div
          className="glass-card"
          style={{
            padding: "2rem",
            display: "flex",
            flexDirection: "column",
            gap: 24,
          }}
        >
          <h2 style={{ fontFamily: "var(--font-voice)", fontSize: 20, color: "var(--navy)", margin: 0 }}>
            About the decision
          </h2>

          <SegmentedField label="Attendees affected" options={ATTENDEE_OPTIONS} value={attendees} onChange={setAttendees} />
          <SegmentedField label="Average salary band" options={SALARY_OPTIONS} value={salary} onChange={setSalary} />
          <SegmentedField label="Average meeting duration" options={DURATION_OPTIONS} value={duration} onChange={setDuration} />
          <SegmentedField label="Times the discussion was repeated" options={REPEAT_OPTIONS} value={repeats} onChange={setRepeats} />
          <SegmentedField label="How long was the decision delayed?" options={DELAY_OPTIONS} value={delay} onChange={setDelay} />

          <p style={{ fontSize: 11.5, color: "var(--text-muted)", margin: 0, lineHeight: 1.6, borderTop: "1px solid var(--border)", paddingTop: 14 }}>
            Hourly rate is the salary band ÷ 2,080 working hours/year, loaded ×1.3 for super and
            overhead, giving a current rate of {formatCurrency(results.hourlyRate)}/hr. Delivery
            impact applies the delay-length multiplier to direct cost, on the assumption that a
            longer delay compounds knock-on disruption rather than staying flat.
          </p>
        </div>

        {/* Results Card */}
        <div
          style={{
            background: "linear-gradient(135deg, var(--navy) 0%, #01072E 100%)",
            color: "#fff",
            borderRadius: "var(--radius-lg)",
            padding: "2rem",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            boxShadow: "var(--shadow-lg)",
          }}
        >
          <div>
            <span
              style={{
                fontSize: 12,
                color: "var(--lilac)",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Estimated Governance Delay Impact
            </span>

            <div style={{ margin: "20px 0" }}>
              <p style={{ fontSize: 13, color: "var(--border-strong)", margin: "0 0 4px" }}>Direct Cost</p>
              <p style={{ fontFamily: "var(--font-voice)", fontSize: 28, color: "#fff", margin: 0, fontWeight: 700 }}>
                {formatCurrency(results.directCost)}
              </p>
              <p style={{ fontSize: 12, color: "var(--border-strong)", margin: "4px 0 0" }}>
                Time actually spent in the room, re-discussing.
              </p>
            </div>

            <div style={{ margin: "20px 0" }}>
              <p style={{ fontSize: 13, color: "var(--border-strong)", margin: "0 0 4px" }}>Estimated Delivery Impact</p>
              <p style={{ fontFamily: "var(--font-voice)", fontSize: 28, color: "var(--lilac)", margin: 0, fontWeight: 700 }}>
                {formatCurrency(results.deliveryImpact)}
              </p>
              <p style={{ fontSize: 12, color: "var(--border-strong)", margin: "4px 0 0" }}>
                Knock-on cost from the delay itself, not just the meetings.
              </p>
            </div>

            <div style={{ margin: "24px 0", paddingTop: 20, borderTop: "1px solid rgba(255,255,255,0.15)" }}>
              <p style={{ fontSize: 13, color: "var(--border-strong)", margin: "0 0 4px" }}>Total Estimated Cost</p>
              <p style={{ fontFamily: "var(--font-voice)", fontSize: 40, color: "#fff", margin: 0, fontWeight: 700 }}>
                {formatCurrency(results.totalCost)}
              </p>
              <span
                style={{
                  display: "inline-block",
                  marginTop: 10,
                  fontSize: 12,
                  fontWeight: 700,
                  padding: "4px 12px",
                  borderRadius: 20,
                  color: interpretation.color,
                  background: interpretation.bg,
                }}
              >
                {interpretation.label}
              </span>
            </div>
          </div>

          <Button href="https://groundworktierup.app/?intent=register" external size="lg" block style={{ marginTop: 16 }}>
            Get Started &rarr;
          </Button>
        </div>
      </div>
    </div>
  );
}
